import React, { useState } from "react";

import {
    Close as CloseIcon,
    DataObjectOutlined as DynamicFieldIcon,
} from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";

import ClipLoader from "react-spinners/ClipLoader";

import SwalAlertComp from "components/SwalAlert";

import { DynamicField, Language } from "../DynamicFieldsSection.types";

interface DeleteDynamicFieldDialogProps {
    open: boolean;
    onClose: () => void;
    dynamicField: DynamicField | null;
    language?: Language;
    onDelete: (
        dynamicFieldId: number,
        force: boolean,
    ) => Promise<{ status: boolean; message?: string }>;
    onDeleteSuccess?: () => void;
}

const DeleteDynamicFieldDialog: React.FC<DeleteDynamicFieldDialogProps> = ({
    open,
    onClose,
    dynamicField,
    language,
    onDelete,
    onDeleteSuccess,
}) => {
    const theme = useTheme();
    const [isDeleting, setIsDeleting] = useState(false);
    const [isForceChecked, setIsForceChecked] = useState(false);

    const handleClose = () => {
        if (!isDeleting) {
            setIsForceChecked(false);
            onClose();
        }
    };

    const handleDelete = async () => {
        if (!dynamicField?.id) return;

        setIsDeleting(true);
        const response = await onDelete(dynamicField.id, isForceChecked);
        setIsDeleting(false);

        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: "Dynamic field deleted successfully",
                icon: "success",
                timer: 3000,
            });
            onDeleteSuccess?.();
            handleClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Typography variant="h6" component="div">
                    Delete Dynamic Field
                </Typography>
                <IconButton
                    onClick={handleClose}
                    disabled={isDeleting}
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                    >
                        This action will permanently delete the dynamic field.
                        Mappings that use this field may stop working. This
                        cannot be undone.
                    </Typography>
                </Box>

                <Stack spacing={3}>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            Dynamic Field Information
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                            }}
                        >
                            <DynamicFieldIcon
                                sx={{ color: "#ed6c02", fontSize: 32 }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography
                                    variant="body1"
                                    noWrap
                                    sx={{ fontWeight: 500 }}
                                >
                                    {dynamicField?.name ?? "—"}
                                </Typography>
                                <Chip
                                    label={
                                        dynamicField?.type === "advanced_mode"
                                            ? "Advanced Mode"
                                            : "Basic Mode"
                                    }
                                    size="small"
                                    sx={{
                                        mt: 0.5,
                                        height: 20,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                    }}
                                    onClick={() => {}}
                                />
                            </Box>
                        </Box>
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isForceChecked}
                                    onChange={(e) =>
                                        setIsForceChecked(e.target.checked)
                                    }
                                    disabled={isDeleting}
                                />
                            }
                            label={
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                    >
                                        Force delete even if the field is in use
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        The dynamic field will be deleted even
                                        when it is used in a mapping. Mappings
                                        that reference it may stop working.
                                    </Typography>
                                </Box>
                            }
                        />
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{ p: 3, gap: 1, justifyContent: "space-between" }}
            >
                <Button
                    onClick={handleClose}
                    disabled={isDeleting}
                    variant="outlined"
                >
                    Cancel
                </Button>

                <div className="position-relative overflow-hidden z-1">
                    {isDeleting && (
                        <div className="circle-loader max-height-45">
                            <ClipLoader color={"#000"} size={17} />
                        </div>
                    )}
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                    >
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteDynamicFieldDialog;
